import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import en from './locales/en.json';
import es from './locales/es.json';

export const SUPPORTED_LANGUAGES = ['es', 'en'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

const STORAGE_KEY = 'i18nextLng';

/**
 * Decide the initial language for visitors without a URL prefix.
 *
 * Priority:
 *  1. Persisted preference in localStorage (user previously chose one).
 *  2. Browser locale: any `es*` → Spanish; everything else → English.
 *
 * The site is based in LATAM but pitches globally, so non-Spanish
 * speakers default to English.
 */
export const resolveInitialLanguage = (): SupportedLanguage => {
  if (typeof window === 'undefined') return 'es';

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'es' || stored === 'en') return stored;
  } catch {
    /* private mode etc. */
  }

  const candidates: string[] = [];
  if (typeof navigator !== 'undefined') {
    if (navigator.languages && navigator.languages.length) {
      candidates.push(...navigator.languages);
    }
    if (navigator.language) candidates.push(navigator.language);
  }

  return candidates.some((l) => l.toLowerCase().startsWith('es')) ? 'es' : 'en';
};

/** Extract the `:lang` segment from a pathname, if present. */
export const extractLangFromPath = (pathname: string): SupportedLanguage | null => {
  const first = pathname.split('/')[1];
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(first)
    ? (first as SupportedLanguage)
    : null;
};

// Use whatever the URL already declares (set by the bootstrap in App.tsx)
// so first paint matches the route, then fall back to detection.
const initialLng: SupportedLanguage =
  (typeof window !== 'undefined' && extractLangFromPath(window.location.pathname)) ||
  resolveInitialLanguage();

i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    es: { translation: es },
  },
  fallbackLng: 'en',
  lng: initialLng,
  interpolation: {
    escapeValue: false,
  },
});

if (typeof document !== 'undefined') {
  document.documentElement.lang = initialLng;
}

export default i18n;
